import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, Activity, Car, Check, Clock, Phone,
  RefreshCw, Search, User, X, MapPin, Eye, Plane,
} from 'lucide-react';
import { toast } from 'sonner';
import { getRideOperations, resolveRide } from '../../services/api';
import { getRideDestination, getRidePickup } from '../../utils/rideFormat';
import { useTableSort, sortRows } from '../../hooks/useTableSort';
import { SortableTableHead } from '../ui/SortableTableHead';

const OPS_RIDE_SORT = {
  id: (r) => r.ride_number || r.ticket_number || '',
  customer: (r) => (r.passenger_name || '').toLowerCase(),
  route: (r) => `${getRidePickup(r)} ${getRideDestination(r)}`.toLowerCase(),
  driver: (r) => (r.driver_name || '').toLowerCase(),
  status: (r) => (r.status || '').toLowerCase(),
  activity: (r) => Number(r.minutes_since_activity ?? -1),
  price: (r) => Number(getRidePriceLek(r) || 0),
};
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';

const statusConfig = {
  pending: { label: 'Në Pritje', bg: 'bg-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-500' },
  accepted: { label: 'Pranuar', bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500' },
  assigned: { label: 'Caktuar', bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500' },
  arriving: { label: 'Duke Arritur', bg: 'bg-purple-100', text: 'text-purple-700', dot: 'bg-purple-500' },
  en_route: { label: 'Në Rrugë', bg: 'bg-purple-100', text: 'text-purple-700', dot: 'bg-purple-500' },
  arrived_pickup: { label: 'Tek Pickup', bg: 'bg-indigo-100', text: 'text-indigo-700', dot: 'bg-indigo-500' },
  in_progress: { label: 'Në Udhëtim', bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500' },
  at_stopover: { label: 'Ndalesë', bg: 'bg-orange-100', text: 'text-orange-700', dot: 'bg-orange-500' },
  passenger_onboard: { label: 'Pasagjeri Hipi', bg: 'bg-cyan-100', text: 'text-cyan-700', dot: 'bg-cyan-500' },
  arrived_destination: { label: 'Tek Destinacioni', bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  scheduled: { label: 'Planifikuar', bg: 'bg-orange-100', text: 'text-orange-700', dot: 'bg-orange-500' },
};

const getStatus = (status) => statusConfig[status] || { label: status, bg: 'bg-gray-100', text: 'text-gray-700', dot: 'bg-gray-500' };

const getRidePriceLek = (ride) => ride.price_lek ?? ride.estimated_price ?? ride.fare ?? 0;

const formatInactiveTime = (minutes) => {
  if (minutes == null) return '—';
  if (minutes >= 1440) return `${Math.floor(minutes / 1440)} ditë`;
  if (minutes >= 60) return `${Math.floor(minutes / 60)} orë`;
  return `${minutes} min`;
};

const RideOperationsPage = ({ basePath = '/admin' }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('stuck');
  const [hours, setHours] = useState('2');
  const [rides, setRides] = useState([]);
  const [counts, setCounts] = useState({ total_active: 0, stuck: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionRide, setActionRide] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { sortKey, sortDir, onSort } = useTableSort('activity', 'desc');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getRideOperations({ tab, hours: parseInt(hours, 10) });
      setRides(data.rides || []);
      setCounts(data.counts || {});
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në ngarkimin e udhëtimeve');
    } finally {
      setLoading(false);
    }
  }, [tab, hours]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const filteredRides = useMemo(() => {
    if (!searchTerm) return rides;
    const q = searchTerm.toLowerCase();
    return rides.filter((ride) => {
      const pickup = getRidePickup(ride).toLowerCase();
      const destination = getRideDestination(ride).toLowerCase();
      return (
        ride.ride_number?.toLowerCase().includes(q) ||
        ride.passenger_name?.toLowerCase().includes(q) ||
        ride.driver_name?.toLowerCase().includes(q) ||
        ride.vehicle?.plate?.toLowerCase().includes(q) ||
        pickup.includes(q) ||
        destination.includes(q)
      );
    });
  }, [rides, searchTerm]);

  const displayedRides = useMemo(
    () => sortRows(filteredRides, sortKey, sortDir, OPS_RIDE_SORT),
    [filteredRides, sortKey, sortDir],
  );

  const openAction = (ride, type) => {
    setActionRide(ride);
    setActionType(type);
    setReason('');
    setAmount(getRidePriceLek(ride) ? String(getRidePriceLek(ride)) : '');
  };

  const closeAction = () => {
    setActionRide(null);
    setActionType(null);
    setReason('');
    setAmount('');
  };

  const handleConfirm = async () => {
    if (!actionRide) return;
    if (actionType === 'cancel' && !reason.trim()) {
      toast.error('Shkruani arsyen e anulimit');
      return;
    }

    try {
      setSubmitting(true);
      await resolveRide(actionRide.id, {
        action: actionType,
        reason: reason.trim() || undefined,
        paymentMethod: 'cash',
        amountReceived: actionType === 'complete' && amount ? parseFloat(amount) : undefined,
      });
      toast.success(actionType === 'cancel' ? 'Udhëtimi u anulua' : 'Udhëtimi u përfundua');
      closeAction();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Veprimi dështoi');
    } finally {
      setSubmitting(false);
    }
  };

  const canManage = (ride) => ride.status && !['completed', 'cancelled'].includes(ride.status);

  const activeNonStuck = Math.max(0, (counts.total_active || 0) - (counts.stuck || 0));

  return (
    <div className="space-y-4 w-full">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl p-4 border shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <div className="text-2xl font-bold text-amber-600">{counts.stuck || 0}</div>
            <div className="text-xs text-gray-500">Të ngrira</div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <Activity className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <div className="text-2xl font-bold text-blue-600">{activeNonStuck}</div>
            <div className="text-xs text-gray-500">Aktive</div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
            <Car className="w-5 h-5 text-gray-600" />
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-700">{counts.total_active || 0}</div>
            <div className="text-xs text-gray-500">Gjithsej aktive</div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
            <Clock className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <div className="text-2xl font-bold text-purple-600">&gt;{hours}h</div>
            <div className="text-xs text-gray-500">Prag i ngrirjes</div>
          </div>
        </div>
      </div>

      {tab === 'stuck' && counts.stuck > 0 && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <p className="font-semibold">{counts.stuck} udhëtime të ngrira</p>
            <p className="text-amber-800 mt-1">
              Këto udhëtime kanë qëndruar në status aktiv më shumë se {hours} orë. Mund t&apos;i anuloni ose t&apos;i përfundoni manualisht.
            </p>
          </div>
        </div>
      )}

      {/* Table Card */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="p-4 border-b bg-gray-50">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-600" />
              Menaxhimi i Udhëtimeve ({filteredRides.length})
            </h3>

            <div className="flex flex-wrap items-center gap-2">
              <div className="flex rounded-lg border border-gray-200 overflow-hidden bg-white">
                {[
                  { id: 'stuck', label: 'Të ngrira', count: counts.stuck },
                  { id: 'active', label: 'Aktive', count: activeNonStuck },
                  { id: 'all', label: 'Të gjitha', count: counts.total_active },
                ].map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setTab(t.id)}
                    className={`px-3 py-2 text-sm font-medium transition-colors ${
                      tab === t.id ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {t.label}
                    {t.count > 0 && (
                      <span className={`ml-1.5 px-1.5 py-0.5 rounded text-xs ${
                        tab === t.id ? 'bg-white/20' : 'bg-gray-100'
                      }`}>
                        {t.count}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              <Select value={hours} onValueChange={setHours}>
                <SelectTrigger className="w-40 h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Stuck &gt; 1 orë</SelectItem>
                  <SelectItem value="2">Stuck &gt; 2 orë</SelectItem>
                  <SelectItem value="4">Stuck &gt; 4 orë</SelectItem>
                  <SelectItem value="8">Stuck &gt; 8 orë</SelectItem>
                  <SelectItem value="24">Stuck &gt; 24 orë</SelectItem>
                </SelectContent>
              </Select>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Kërko..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-4 py-2 text-sm border rounded-lg w-44 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <button
                onClick={fetchData}
                disabled={loading}
                className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition"
                title="Rifresko"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <SortableTableHead label="ID" field="id" activeField={sortKey} sortDir={sortDir} onSort={onSort} />
                <SortableTableHead label="Klienti" field="customer" activeField={sortKey} sortDir={sortDir} onSort={onSort} />
                <SortableTableHead label="Rruga" field="route" activeField={sortKey} sortDir={sortDir} onSort={onSort} />
                <SortableTableHead label="Shoferi" field="driver" activeField={sortKey} sortDir={sortDir} onSort={onSort} />
                <SortableTableHead label="Statusi" field="status" activeField={sortKey} sortDir={sortDir} onSort={onSort} align="center" />
                <SortableTableHead label="Aktiviteti" field="activity" activeField={sortKey} sortDir={sortDir} onSort={onSort} align="center" />
                <SortableTableHead label="Çmimi" field="price" activeField={sortKey} sortDir={sortDir} onSort={onSort} align="right" />
                <th className="text-center px-4 py-3 text-xs font-bold text-gray-500 uppercase">Veprime</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && displayedRides.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-12 text-gray-400">
                    <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin opacity-50" />
                    <p>Duke ngarkuar udhëtimet...</p>
                  </td>
                </tr>
              ) : displayedRides.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-12 text-gray-400">
                    <Car className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>Nuk ka udhëtime për këtë filtër</p>
                  </td>
                </tr>
              ) : (
                displayedRides.map((ride) => {
                  const status = getStatus(ride.status);
                  const pickup = getRidePickup(ride);
                  const destination = getRideDestination(ride);
                  const priceLek = getRidePriceLek(ride);
                  const inactive = formatInactiveTime(ride.minutes_since_activity);

                  return (
                    <tr
                      key={ride.id}
                      className={`hover:bg-gray-50 transition-colors cursor-pointer ${
                        ride.is_stuck ? 'bg-amber-50/40' : ''
                      }`}
                      onClick={() => navigate(`${basePath}/rides/${ride.id}`)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <span className="font-mono text-sm font-medium text-gray-700">
                            {ride.ride_number || `#${ride.id?.slice(-6).toUpperCase()}`}
                          </span>
                          <div className="flex flex-wrap gap-1">
                            {ride.is_airport && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-700">
                                <Plane className="w-3 h-3" />
                                Aeroport
                              </span>
                            )}
                            {ride.is_stuck && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800">
                                E ngrirë
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <User className="w-4 h-4 text-gray-500" />
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-gray-900 text-sm truncate">
                              {ride.passenger_name || 'Pasagjer'}
                            </div>
                            {ride.passenger_phone && (
                              <div className="text-xs text-gray-500 flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {ride.passenger_phone}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-3 max-w-[220px]">
                        <div className="flex items-start gap-1.5 text-sm text-gray-600">
                          <MapPin className="w-3.5 h-3.5 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="truncate" title={pickup}>{pickup}</span>
                        </div>
                        <div className="flex items-start gap-1.5 text-sm text-gray-600 mt-1">
                          <MapPin className="w-3.5 h-3.5 text-red-600 mt-0.5 flex-shrink-0" />
                          <span className="truncate" title={destination}>{destination}</span>
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        {ride.driver_name ? (
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                              <Car className="w-4 h-4 text-yellow-600" />
                            </div>
                            <div className="min-w-0">
                              <div className="font-medium text-gray-900 text-sm truncate">{ride.driver_name}</div>
                              {ride.vehicle?.plate && (
                                <div className="text-xs text-gray-500 font-mono">{ride.vehicle.plate}</div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400 italic">Pa caktuar</span>
                        )}
                      </td>

                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                          {status.label}
                        </span>
                      </td>

                      <td className="px-4 py-3 text-center">
                        <div className={`text-sm font-medium ${
                          ride.is_stuck ? 'text-amber-700' : 'text-gray-600'
                        }`}>
                          {inactive}
                        </div>
                        <div className="text-[10px] text-gray-400 uppercase tracking-wide">pa aktivitet</div>
                      </td>

                      <td className="px-4 py-3 text-right">
                        <div className="font-bold text-gray-900">{priceLek.toLocaleString()} L</div>
                        <div className="text-xs text-gray-500 capitalize">{ride.payment_method || 'cash'}</div>
                      </td>

                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-1">
                          {ride.passenger_phone && (
                            <a
                              href={`tel:${ride.passenger_phone}`}
                              className="p-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition"
                              title="Telefono"
                            >
                              <Phone className="w-4 h-4" />
                            </a>
                          )}
                          <button
                            onClick={() => navigate(`${basePath}/rides/${ride.id}`)}
                            className="p-1.5 bg-yellow-100 hover:bg-yellow-200 text-yellow-700 rounded-lg transition"
                            title="Detaje"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {canManage(ride) && (
                            <>
                              <button
                                onClick={() => openAction(ride, 'complete')}
                                className="p-1.5 bg-green-100 hover:bg-green-200 text-green-600 rounded-lg transition"
                                title="Përfundo"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => openAction(ride, 'cancel')}
                                className="p-1.5 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition"
                                title="Anulo"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {filteredRides.length > 0 && (
          <div className="px-4 py-3 bg-gray-50 border-t text-sm text-gray-500 flex flex-wrap justify-between items-center gap-2">
            <span>Duke shfaqur {filteredRides.length} udhëtime</span>
            <div className="flex flex-wrap gap-4">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-amber-500" />
                Të ngrira: {counts.stuck || 0}
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-blue-500" />
                Aktive: {activeNonStuck}
              </span>
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!actionRide} onOpenChange={(open) => !open && closeAction()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'cancel' ? 'Anulo udhëtimin' : 'Përfundo udhëtimin'}
            </DialogTitle>
          </DialogHeader>
          {actionRide && (
            <div className="space-y-3 text-sm">
              <p className="text-gray-600">
                <strong>{actionRide.ride_number}</strong> — {actionRide.passenger_name}
              </p>
              {actionType === 'cancel' ? (
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Arsyeja (e detyrueshme)</label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="w-full border rounded-lg p-2 text-sm min-h-[80px]"
                    placeholder="p.sh. shoferi nuk përgjigjet, gabim sistemi..."
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Shuma e marrë (Lekë)</label>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="w-full border rounded-lg p-2 text-sm"
                  />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeAction} disabled={submitting}>
              Mbyll
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={submitting}
              className={actionType === 'cancel' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}
            >
              {submitting ? 'Duke procesuar...' : 'Konfirmo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RideOperationsPage;
